import {
  BellAlertIcon,
  BoltIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentCheckIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid';
import { useBrandConfig } from '~/config/branding';

const medCredVaultFeatures = [
  {
    name: 'Free Secure Credential Storage',
    description:
      'HIPAA-compliant digital vault keeps all your licenses, certifications, and credentials in one secure place with 24/7 access.',
    icon: ShieldCheckIcon,
  },
  {
    name: 'AI-Powered Career Insights',
    description:
      'Real-time salary benchmarking, market analytics, and compensation trends help you optimize your earnings and career decisions.',
    icon: ChartBarIcon,
  },
  {
    name: 'Automated Compliance Tracking',
    description:
      'Never miss a renewal again. Get alerts for expiring licenses, ongoing monitoring (OIG, SAM, NPDB, OFAC), and compliance requirements.',
    icon: BellAlertIcon,
  },
  {
    name: 'Board-Ready Credential Packets',
    description:
      'Instantly generate and download comprehensive credential packets that meet facility requirements, saving weeks of paperwork.',
    icon: DocumentCheckIcon,
  },
  {
    name: 'Gold-Verified Credentials',
    description:
      'Achieve instant trust with third-party CVO verification, giving employers confidence and accelerating your hiring process.',
    icon: ShieldCheckIcon,
  },
  {
    name: 'Real-Time Market Data',
    description:
      'Access demand trends by specialty and location to identify high-paying opportunities and competitive advantages in your field.',
    icon: ChartBarIcon,
  },
];

const healthcareLxFeatures = [
  {
    name: 'Earn 100% of Your Pay',
    description:
      'No agency fees or middlemen taking cuts. Connect directly with employers and keep every dollar you earn.',
    icon: CurrencyDollarIcon,
  },
  {
    name: 'AI-Powered Job Matching',
    description:
      'Get real-time job offers matched to your credentials, preferences, and availability using 20,000+ data points.',
    icon: BoltIcon,
  },
  {
    name: 'Full Schedule Control',
    description:
      'Work when and where you want with no long-term contracts. Choose shifts that fit your lifestyle and career goals.',
    icon: ClockIcon,
  },
  {
    name: 'Faster Credentialing',
    description:
      'Pre-filled facility-specific forms sync automatically from MedCredVault, eliminating weeks of onboarding delays.',
    icon: DocumentCheckIcon,
  },
  {
    name: 'Direct Employer Access',
    description:
      'Connect instantly with hospitals, clinics, and healthcare facilities without intermediaries or complicated applications.',
    icon: BuildingOfficeIcon,
  },
  {
    name: 'Flexible Marketplace',
    description:
      'Access both internal organizational shifts and external opportunities, maximizing your options for work and income.',
    icon: UserGroupIcon,
  },
];

export const Benefits = () => {
  // [MCV-1490] Each brand's public site shows only its own section.
  const { brand } = useBrandConfig();
  return (
    <div className="bg-white">
      {/* MedCredVault Section */}
      {brand === 'mcv' && (
        <div className="py-10 sm:py-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl">
              <p className="text-base/7 font-semibold text-primary-50 text-left">For Providers</p>
              <h2 className="mt-2 text-4xl font-semibold tracking-tight text-left text-pretty text-grey-100 sm:text-5xl">
                MedCredVault
              </h2>
              <p className="mt-6 text-lg/8 text-grey-300 text-left">
                Your AI-powered career management platform. Organize credentials, track compliance,
                and gain insights—all in one secure vault designed to put you in control of your
                healthcare career.
              </p>
            </div>
            <dl className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 text-base/7 text-grey-300 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-16">
              {medCredVaultFeatures.map((feature) => (
                <div key={feature.name} className="relative pl-9">
                  <dt className="inline font-semibold text-grey-100">
                    <feature.icon
                      aria-hidden="true"
                      className="absolute top-1 left-1 size-5 text-primary-50"
                    />
                    {feature.name}
                  </dt>{' '}
                  <dd className="inline">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}

      {/* HealthcareLX Section */}
      {brand === 'hlx' && (
        <div className="py-10 sm:py-32">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl">
              <p className="text-base/7 font-semibold text-primary-50 text-left">For Providers</p>
              <h2 className="mt-2 text-4xl font-semibold tracking-tight text-left text-pretty text-grey-100 sm:text-5xl">
                HealthcareLX
              </h2>
              <p className="mt-6 text-lg/8 text-grey-300 text-left">
                The AI-driven marketplace that connects you directly with healthcare employers. Work
                smarter with instant job matching, transparent pay, and seamless credentialing—no
                agencies, no markup, just opportunities.
              </p>
            </div>
            <dl className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 text-base/7 text-grey-300 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-16">
              {healthcareLxFeatures.map((feature) => (
                <div key={feature.name} className="relative pl-9">
                  <dt className="inline font-semibold text-grey-100">
                    <feature.icon
                      aria-hidden="true"
                      className="absolute top-1 left-1 size-5 text-good"
                    />
                    {feature.name}
                  </dt>{' '}
                  <dd className="inline">{feature.description}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
};
